import { BoltIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Button } from '@/components/Button'
import Label from '@/components/Label'
import Modal from '@/components/Modal'
import TextInput from '@/components/TailwindUI/TextInput'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'

import { partialFeatureSchema } from './formikSchema'
import messages from './messages'

const initialValues = {
  name: '',
  type: '',
  trigger: ''
}

interface CreateFeatureModalProps {
  isOpen?: boolean
  isEditing?: boolean
  formValues?: Partial<typeof initialValues>
  onSubmit: (values: any) => void
  onClose: () => void
}

const CreateFeatureModal = ({
  isOpen = true,
  isEditing = false,
  formValues,
  onSubmit,
  onClose
}: CreateFeatureModalProps) => {
  const intl = useIntl()
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: { ...initialValues, ...formValues },
    resolver: yupResolver(partialFeatureSchema)
  })

  const handleClose = () => onClose()

  // default values are cached and must be invalidated whenever they change
  useEffect(() => reset({ ...formValues }), [reset, formValues])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" className="max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mx-8 my-10">
          <div className="flex items-center">
            {isEditing ? (
              <PencilSquareIcon className="h-[40px] w-[40px] text-gray-400" aria-hidden="true" />
            ) : (
              <BoltIcon className="h-[40px] w-[40px] text-gray-400" aria-hidden="true" />
            )}
            <h3 className="ml-3 text-2xl leading-6 text-gray-900">
              {isEditing ? 'Rename' : 'Create'} {agentsFlagOn ? 'Capability' : 'Feature'}
            </h3>
          </div>
          <div className="mt-12">
            <Label htmlFor="name" required>
              Name
            </Label>
            <TextInput
              className="mt-2"
              id="name"
              placeholder={`Name your ${agentsFlagOn ? 'capability' : 'feature'}`}
              error={errors.name?.message}
              {...register('name')}
            />
          </div>
          <div className="mt-12 flex items-center justify-end gap-x-6">
            <Button color="transparent" onClick={() => handleClose()}>
              {intl.formatMessage(messages.cancel)}
            </Button>
            <Button color="primary">{intl.formatMessage(messages.continue)}</Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default CreateFeatureModal
